import React, { useMemo } from 'react';
import { useCosts } from '../../context/CostsContext';
import { useLanguage } from '../../i18n';
import { calculateKaufnebenkosten } from '../../data/acquisitionCostConfig';
import './InputComponents.css';

function PurchaseAndFinancing() {
  const { costs, updateProperty, updatePurchase, updateLoan } = useCosts();
  const { t } = useLanguage();
  const { equityCapital, debtCapital } = costs.purchase;
  const { purchasePrice } = costs.property;
  const { interestRate, zinsbindungYears, tilgungPercent, tilgungsfreieJahre, refinancingRateSpread } = costs.loan;

  // Calculate derived metrics
  const totalCapital = equityCapital + debtCapital;
  const ltvPercent = purchasePrice > 0 ? ((debtCapital / purchasePrice) * 100).toFixed(1) : 0;
  const debtToEquityRatio = equityCapital > 0 ? (debtCapital / equityCapital).toFixed(2) : '∞';

  // Auto-calculated acquisition costs based on Bundesland and purchase price
  const autoCalc = useMemo(() => {
    return calculateKaufnebenkosten(costs.property.bundesland, purchasePrice, debtCapital);
  }, [costs.property.bundesland, purchasePrice, debtCapital]);

  // Use manual override if > 0, otherwise auto-calculated
  const effectiveGrErwSt = costs.startup.grunderwerbsteuer || autoCalc.grunderwerbsteuer;
  const effectiveNotar = costs.startup.notarkosten || autoCalc.notar;
  const effectiveGrundbuch = costs.startup.grundbuchkosten || autoCalc.grundbuch;
  const effectiveMakler = costs.startup.maklerkosten || autoCalc.makler;
  const effectiveKaufnebenkosten = effectiveGrErwSt + effectiveNotar + effectiveGrundbuch + effectiveMakler;

  const startupTotal = effectiveKaufnebenkosten + costs.startup.renovationBudget;

  const cashAfterPurchase = totalCapital - purchasePrice - startupTotal;

  // Annual mortgage payment (Annuität)
  const annualRate = (interestRate + tilgungPercent) / 100;
  const annualPayment = debtCapital * annualRate;

  return (
    <div className="input-component">
      <div className="input-grid">
        <div className="input-group">
          <label htmlFor="purchasePrice">
            {t('purchase.propertyPrice')}
            <span className="tooltip">{t('purchase.propertyPrice.tooltip')}</span>
          </label>
          <div className="input-with-currency">
            <input
              id="purchasePrice"
              type="number"
              value={purchasePrice}
              onChange={(e) => updateProperty({ purchasePrice: parseFloat(e.target.value) || 0 })}
              step="1000"
            />
            <span className="currency">€</span>
          </div>
        </div>

        <div className="input-group">
          <label htmlFor="equityCapital">
            {t('purchase.equityCapital')}
            <span className="tooltip">{t('purchase.equityCapital.tooltip')}</span>
          </label>
          <div className="input-with-currency">
            <input
              id="equityCapital"
              type="number"
              value={equityCapital}
              onChange={(e) => updatePurchase({ equityCapital: parseFloat(e.target.value) || 0 })}
              step="1000"
            />
            <span className="currency">€</span>
          </div>
        </div>

        <div className="input-group">
          <label htmlFor="debtCapital">
            {t('purchase.debtCapital')}
            <span className="tooltip">{t('purchase.debtCapital.tooltip')}</span>
          </label>
          <div className="input-with-currency">
            <input
              id="debtCapital"
              type="number"
              value={debtCapital}
              onChange={(e) => updatePurchase({ debtCapital: parseFloat(e.target.value) || 0 })}
              step="1000"
            />
            <span className="currency">€</span>
          </div>
        </div>
      </div>

      <h4 style={{ marginTop: '20px' }}>{t('purchase.loanSettings')}</h4>
      <div className="input-grid">
        <div className="input-group">
          <label htmlFor="interestRate">
            {t('purchase.interestRate')}
            <span className="tooltip">{t('purchase.interestRate.tooltip')}</span>
          </label>
          <div className="input-with-currency">
            <input
              id="interestRate"
              type="number"
              value={interestRate}
              onChange={(e) => updateLoan({ interestRate: parseFloat(e.target.value) || 0 })}
              step="0.05"
              min="0"
              max="15"
            />
            <span className="currency">%</span>
          </div>
        </div>

        <div className="input-group">
          <label htmlFor="zinsbindungYears">
            {t('purchase.zinsbindung')}
            <span className="tooltip">{t('purchase.zinsbindung.tooltip')}</span>
          </label>
          <div className="input-with-currency">
            <input
              id="zinsbindungYears"
              type="number"
              value={zinsbindungYears}
              onChange={(e) => updateLoan({ zinsbindungYears: parseInt(e.target.value) || 10 })}
              step="1"
              min="1"
              max="30"
            />
            <span className="currency">{t('common.years')}</span>
          </div>
        </div>

        <div className="input-group">
          <label htmlFor="tilgungPercent">
            {t('purchase.tilgung')}
            <span className="tooltip">{t('purchase.tilgung.tooltip')}</span>
          </label>
          <div className="input-with-currency">
            <input
              id="tilgungPercent"
              type="number"
              value={tilgungPercent}
              onChange={(e) => updateLoan({ tilgungPercent: parseFloat(e.target.value) || 0 })}
              step="0.5"
              min="0"
              max="10"
            />
            <span className="currency">%</span>
          </div>
        </div>

        <div className="input-group">
          <label htmlFor="tilgungsfreieJahre">
            {t('purchase.tilgungsfreieJahre')}
            <span className="tooltip">{t('purchase.tilgungsfreieJahre.tooltip')}</span>
          </label>
          <div className="input-with-currency">
            <input
              id="tilgungsfreieJahre"
              type="number"
              value={tilgungsfreieJahre}
              onChange={(e) => updateLoan({ tilgungsfreieJahre: parseInt(e.target.value) || 0 })}
              step="1"
              min="0"
              max="10"
            />
            <span className="currency">{t('common.years')}</span>
          </div>
        </div>

        <div className="input-group">
          <label htmlFor="refinancingRateSpread">
            {t('purchase.refinancingSpread')}
            <span className="tooltip">{t('purchase.refinancingSpread.tooltip')}</span>
          </label>
          <div className="input-with-currency">
            <input
              id="refinancingRateSpread"
              type="number"
              value={refinancingRateSpread}
              onChange={(e) => updateLoan({ refinancingRateSpread: parseFloat(e.target.value) || 0 })}
              step="0.25"
              min="-5"
              max="10"
            />
            <span className="currency">%</span>
          </div>
        </div>
      </div>

      <div className="summary-box">
        <h4>{t('purchase.capitalStructure')}</h4>
        <div className="summary-row">
          <span className="summary-label">{t('purchase.totalCapitalRaised')}:</span>
          <span className="summary-value">€{totalCapital.toLocaleString('de-DE')}</span>
        </div>
        <div className="summary-row">
          <span className="summary-label">{t('purchase.ltvRatio')}:</span>
          <span className={`summary-value ${ltvPercent > 90 ? 'warning' : ''}`}>
            {ltvPercent}%
          </span>
        </div>
        <div className="summary-row">
          <span className="summary-label">{t('purchase.debtToEquityRatio')}:</span>
          <span className="summary-value">{debtToEquityRatio}:1</span>
        </div>
        <div className="summary-row">
          <span className="summary-label">{t('purchase.annualMortgagePayment')}:</span>
          <span className="summary-value">€{annualPayment.toLocaleString('de-DE', { maximumFractionDigits: 0 })}</span>
        </div>

        <h4 style={{ marginTop: '20px' }}>{t('purchase.useOfFunds')}</h4>
        <div className="summary-row">
          <span className="summary-label">{t('purchase.propertyPurchase')}:</span>
          <span className="summary-value">-€{purchasePrice.toLocaleString('de-DE')}</span>
        </div>
        <div className="summary-row">
          <span className="summary-label">{t('purchase.kaufnebenkosten')}:</span>
          <span className="summary-value">-€{effectiveKaufnebenkosten.toLocaleString('de-DE', { maximumFractionDigits: 0 })}</span>
        </div>
        {costs.startup.renovationBudget > 0 && (
          <div className="summary-row">
            <span className="summary-label">{t('purchase.renovationBudget')}:</span>
            <span className="summary-value">-€{costs.startup.renovationBudget.toLocaleString('de-DE')}</span>
          </div>
        )}
        <div className="summary-row highlight">
          <span className="summary-label">{t('purchase.cashRemaining')}:</span>
          <span className={`summary-value ${cashAfterPurchase < 0 ? 'warning' : ''}`}>
            €{cashAfterPurchase.toLocaleString('de-DE', { maximumFractionDigits: 0 })}
          </span>
        </div>

        {cashAfterPurchase < 0 && (
          <div className="warning-message">
            {t('purchase.negativeCash').replace('{amount}', Math.abs(cashAfterPurchase).toLocaleString('de-DE', { maximumFractionDigits: 0 }))}
          </div>
        )}

        <div className="helper-text">
          <strong>{t('purchase.howItWorks')}:</strong> {t('purchase.howItWorksText')
            .replace('{equity}', equityCapital.toLocaleString('de-DE'))
            .replace('{debt}', debtCapital.toLocaleString('de-DE'))
            .replace('{remaining}', Math.max(0, cashAfterPurchase).toLocaleString('de-DE', { maximumFractionDigits: 0 }))}
        </div>
      </div>
    </div>
  );
}

export default PurchaseAndFinancing;
